import React from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  FirstPage,
  LastPage,
} from '@mui/icons-material';

/**
 * Reusable Pagination Component
 * Provides navigation through paginated data with customizable items per page
 */
const PaginationComponent = ({
  currentPage = 1,
  totalItems = 0,
  itemsPerPage = 12,
  onPageChange = () => {},
  onItemsPerPageChange = () => {},
  showPageInfo = true,
  variant = 'standard', // 'standard', 'minimal'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handleFirstPage = () => onPageChange(1);
  const handlePrevPage = () => onPageChange(Math.max(1, currentPage - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages, currentPage + 1));
  const handleLastPage = () => onPageChange(totalPages);

  if (totalPages <= 1) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        mt: 4,
        p: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: variant === 'minimal' ? 'transparent' : 'grey.50',
      }}
    >
      {/* Left: Page Info */}
      {showPageInfo && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {startItem}-{endItem} of {totalItems}
          </Typography>
          {!isMobile && (
            <Select
              value={itemsPerPage}
              onChange={(e) => {
                onItemsPerPageChange(e.target.value);
                onPageChange(1); // Reset to page 1
              }}
              size="small"
              sx={{ ml: 1 }}
            >
              <MenuItem value={6}>6 per page</MenuItem>
              <MenuItem value={12}>12 per page</MenuItem>
              <MenuItem value={24}>24 per page</MenuItem>
              <MenuItem value={48}>48 per page</MenuItem>
            </Select>
          )}
        </Box>
      )}

      {/* Center: Page Counter */}
      {!isMobile && (
        <Typography variant="body2" color="text.secondary">
          Page {currentPage} of {totalPages}
        </Typography>
      )}

      {/* Right: Navigation */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <IconButton
          size="small"
          onClick={handleFirstPage}
          disabled={currentPage === 1}
          title="First page"
        >
          <FirstPage />
        </IconButton>
        <IconButton
          size="small"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          title="Previous page"
        >
          <KeyboardArrowLeft />
        </IconButton>

        {/* Page Numbers (desktop only) */}
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 0.5, mx: 1 }}>
            {[...Array(Math.min(5, totalPages))].map((_, idx) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = idx + 1;
              } else if (currentPage <= 3) {
                pageNum = idx + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + idx;
              } else {
                pageNum = currentPage - 2 + idx;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'contained' : 'text'}
                  size="small"
                  onClick={() => onPageChange(pageNum)}
                  sx={{
                    minWidth: 32,
                    height: 32,
                    fontWeight: currentPage === pageNum ? 600 : 400,
                  }}
                >
                  {pageNum}
                </Button>
              );
            })}
          </Box>
        )}

        <IconButton
          size="small"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          title="Next page"
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleLastPage}
          disabled={currentPage === totalPages}
          title="Last page"
        >
          <LastPage />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PaginationComponent;
